import type { FC, ReactNode } from 'react'

import { useRef } from 'react'

import { gsap, useGsap, prefersReducedMotion } from '@/lib/gsap'
import { media } from '@/constants/media'
import { Rule } from '@/components/generals/rule'
import { Reveal } from '@/components/generals/reveal'
import { SplitText } from '@/components/generals/split-text'
import { ScrollFigure } from '@/components/generals/scroll-figure'
import { principles } from '@/constants/studio'

/* The list used to run the full width with a very tall gap under each row,
   which is where the page felt empty. It is now a two-part composition: a
   sticky plate holds the left column while the principles pass it, and each
   row is a ruled band whose numeral slides against the copy beside it. */
export const StudioPrinciples: FC = (): ReactNode => {
    const scope = useRef<HTMLElement>(null)

    useGsap(() => {
        const root = scope.current

        if (!root || prefersReducedMotion()) {
            return
        }

        const rows = root.querySelectorAll<HTMLElement>('[data-principle-row]')

        /* The numerals used to be scrubbed against their own row, which meant
           each one sat at a different height depending on where the scroll had
           reached. They are fixed to the heading's baseline now and simply
           arrive with it, so the column reads as a numbered list. */
        rows.forEach((row) => {
            const numeral = row.querySelector<HTMLElement>('[data-principle-index]')

            if (!numeral) {
                return
            }

            gsap.fromTo(numeral,
                { opacity: 0, x: -14 },
                {
                    opacity: 1,
                    x: 0,
                    duration: 0.9,
                    ease: 'noma',
                    scrollTrigger: { trigger: row, start: 'top 88%', once: true }
                }
            )
        })
    }, scope, [])

    return (
        <section
            id='studio-principles'
            ref={scope}
            aria-labelledby='studio-principles-title'
            className='relative z-10 bg-paper text-ink'
        >
            <div className='mx-auto flex max-w-[1920px] flex-col gap-10 px-5 sec lg:gap-14 lg:px-[33px]'>
                <div className='grid gap-6 lg:grid-cols-4 lg:items-end lg:gap-5'>
                    <SplitText
                        as='h2'
                        id='studio-principles-title'
                        text='What guides us.'
                        stagger={0.026}
                        className='t-display text-ink lg:col-span-3'
                        style={{ fontSize: 'clamp(45px, 6vw, 80px)' }}
                    />

                    <Reveal from='up' distance={20} delay={0.2}>
                        <p className='t-cond-soft max-w-[32ch] text-[13px] leading-[1.5] text-ash lg:pb-2'>
                            Four rules we have never broken, written down so you can hold us to them.
                        </p>
                    </Reveal>
                </div>

                <div className='grid gap-10 lg:grid-cols-4 lg:items-start lg:gap-5'>
                    {/* The plate stays with the reader while the rules pass it,
                        which is what closes the gap the full-width list left. */}
                    <div className='lg:sticky lg:top-[calc(var(--nav-h)+28px)]'>
                        <ScrollFigure
                            src={media.editorial.moodboard}
                            alt='A moodboard of material swatches and printed proofs under raking light'
                            aspect='aspect-[4/5]'
                            drift={5}
                            index='(P)'
                            caption='The bench, most mornings'
                        />
                    </div>

                    <div className='flex flex-col lg:col-span-3'>
                        {principles.map((principle, index) => (
                            <div
                                key={principle.index}
                                data-principle-row
                                className='group/rule flex flex-col gap-5 pb-8 lg:gap-6 lg:pb-12'
                            >
                                <Rule delay={index * 0.06} />

                                {/* A fixed first column and a shared type size
                                    are what keep all four numerals on the same
                                    left edge and the same line as their title. */}
                                <div className='grid grid-cols-[3rem_1fr] items-baseline gap-4 lg:grid-cols-[4.5rem_1fr] lg:gap-8'>
                                    <span
                                        data-principle-index
                                        aria-hidden='true'
                                        className='t-display nums block text-brand'
                                        style={{ fontSize: 'clamp(26px, 2.8vw, 38px)' }}
                                    >
                                        {principle.index}
                                    </span>

                                    <Reveal
                                        from='up'
                                        distance={38}
                                        delay={0.08}
                                        className='flex flex-col gap-3'
                                    >
                                        <h3
                                            className='t-display text-ink transition-colors duration-500 group-hover/rule:text-brand'
                                            style={{ fontSize: 'clamp(26px, 2.8vw, 38px)' }}
                                        >
                                            {principle.title}
                                        </h3>

                                        <p className='t-cond-soft max-w-[58ch] text-[15px] leading-[1.45] text-ash'>
                                            {principle.copy}
                                        </p>
                                    </Reveal>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    )
}
